// Production specs, their files, released revisions and change notices (ERP-PLAN.md §5.11), spread into
// `mutations` (shared/mutations.ts). Staff edit drafts and upload files; only admins release. Released revisions
// have no mutations at all (and migration 013 refuses changes to them), so "Rev C" always means the same text and
// files. Rules and releases: server/src/mutations/specs.ts; uploads and downloads: server/src/specs.ts.
import { z } from "zod";
import { ADMIN, create, custom, remove, STAFF, update } from "./mutation-kit.ts";
import { SPEC_FILE_KINDS } from "./specs.ts";

/** Markdown as typed (not trimmed), with Windows line endings made plain so drafts compare cleanly. */
const markdown = z
  .string()
  .max(500_000, "That's too long for one spec")
  .transform((s) => s.replace(/\r\n?/g, "\n"));

export const specFields = z.object({
  draft_md: markdown,
  draft_md_zh: markdown,
  /** HHL only (costs, other vendors, open questions): never released, never shown to vendors. */
  internal_md: markdown,
});

export const specFileFields = z.object({
  spec_id: z.uuid(),
  kind: z.enum(SPEC_FILE_KINDS),
  name: z.string().trim().min(1, "Name the file").max(200),
  position: z.number().int().min(0).max(1_000_000),
  /** Left out of the draft: the next release won't include it (released revisions keep theirs). */
  in_draft: z.boolean(),
});

export const CHANGE_EFFECTIVE = ["next_po", "date"] as const;
export const changeNoticeFields = z.object({
  title: z.string().trim().min(1, "Give it a title").max(300),
  body_md: markdown,
  effective: z.enum(CHANGE_EFFECTIVE),
  effective_date: z.iso.date("Use a date like 2026-12-31").nullable(),
});

/** Why a revision was released: required, shown in its history and to vendors. */
/** What changed in this revision: needed for every release but an item's first (server/src/item-release.ts). */
export const changeNote = z.string().trim().max(5000);

/** draftStamp() (shared/specs.ts) of the draft the release dialog showed. */
const stamp = z.string().regex(/^[0-9a-f]{16}$/, "Not a draft stamp");

export const specMutations = {
  /** One spec per item. */
  "spec.create": create("spec", specFields.partial().extend({ item_id: z.uuid() }), STAFF),
  "spec.update": update("spec", specFields, STAFF),
  "spec_file.create": create("spec_file", specFileFields.partial().required({ spec_id: true, name: true }), STAFF),
  "spec_file.update": update("spec_file", specFileFields.omit({ spec_id: true }), STAFF),
  /** Only a file no revision has released; leave the others out of the draft instead. */
  "spec_file.remove": remove("spec_file", STAFF),
  /**
   * Freezes the draft text and each in-draft file's newest upload as the next revision (A, B…), if the draft is
   * still what the admin reviewed (`stamp`). Returns { id, rev }.
   */
  "spec.release": custom(z.object({ spec_id: z.uuid(), change_note: changeNote, stamp }), ADMIN),

  "change_notice.create": create("change_notice", changeNoticeFields.partial().required({ title: true }), STAFF),
  /** Drafts only: a released notice is a record. */
  "change_notice.update": update("change_notice", changeNoticeFields, STAFF),
  "change_notice.remove": remove("change_notice", STAFF),
  "change_notice_item.create": create("change_notice_item", z.object({ change_notice_id: z.uuid(), item_id: z.uuid() }), STAFF),
  "change_notice_item.remove": remove("change_notice_item", STAFF),
  /**
   * Releases the notice and, linked to it, the next revision of each affected item's spec whose draft differs
   * from its current revision (all or none). `expect` is what the dialog showed: every affected item, with its
   * spec's draft stamp (null: no spec); anything else refuses. Returns { number, released: [{ item_id, sku, rev }],
   * unchanged: [...] }.
   */
  "change_notice.release": custom(z.object({ id: z.uuid(), expect: z.record(z.uuid(), stamp.nullable()) }), ADMIN),
} as const;
